import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {FormArray, FormBuilder, FormGroup, Validators} from '@angular/forms';
import {NotificationsService, ProjectService} from '@catcore/services';
import {GenericResourceApiService} from '@catfeatures/services/generic-resource-api.service';
import {ParametersService} from '@catmodules/models/parameters/services/parameters.service';
import {Parameter} from '@catmodules/models/parameters/types';
import {ActiveModal} from '@catmodules/widgets/slide-in-modal/utils/modal-ref';
import {StyleType} from '@catui/types';
import {Service} from 'app/modules/models/units/types';
import {Observable} from 'rxjs/Observable';
import {isNullOrUndefined} from 'util';
import {extractDataFromModel} from "@catcore/utils";
import {Subscription} from "rxjs/Subscription";
import {Project} from "@catcore/types/api-v2";

/**
 * Provides a modal form to edit feature or project units. The editable parts
 * include e.g. location address, update group, and parameter values.
 */
@Component({
  selector: 'catapp-modify-unit',
  templateUrl: './modify-service.component.html',
  styleUrls: ['./modify-service.component.html.scss'],
})
export class ModifyServiceComponent implements OnInit, OnDestroy {
  @Output() afterDelete = new EventEmitter();
  @Output() afterUpdate = new EventEmitter<Service>();

  @Input() enableDelete = true;

  @Input()
  get serviceEntity(): Service {
    return this._serviceEntity;
  }

  set serviceEntity(value: Service) {
    this._serviceEntity = value;
    if (!isNullOrUndefined(this._serviceEntity)) {
      this.form.patchValue({
        virtual_location_id: this._serviceEntity.virtual_location_id,
      });

      if (!isNullOrUndefined(this._serviceEntity._links.partnumber)) {
        this.partnumber_options = this.buildPartnumberOptions(this._serviceEntity);
        this.form.patchValue( {
          partnumber: this._serviceEntity._links.partnumber.href,
        });
      }

      if (this.hasParameters()) {
        this.parameters = this._serviceEntity._embedded.parameters;
        const formArray = this.form.controls['parameters'] as FormArray;
        while (formArray.length !== 0) {
          formArray.removeAt(0);
        }
        this._serviceEntity._embedded.parameters.forEach((param) => {
          const formControl = this.formBuilder.group({
            key: ['', []],
            value: ['', []],
          });
          formArray.push(formControl);
          formControl.get('key').setValue(param.key);
          formControl.get('value').setValue(param.value);
        });
      }
    }
  }

  labels = {
    part_number: 'Part Number',
    description: 'Description',
    update_group: 'Update Group',
    location_address: 'Location Address',
    virtual_location_id: 'Virtual Location Id',
  };

  form: FormGroup;
  styleType = StyleType;
  parameters: Parameter[];
  fileList: any[] = [];
  partnumber_options: any[] = [];

  private _serviceEntity: Service;
  private _fileListSubscription: Subscription;
  private _project: Project;
  private _projectSubscription;

  constructor(private formBuilder: FormBuilder,
              private genericResourceService: GenericResourceApiService,
              private activeModal: ActiveModal,
              private parametersService: ParametersService,
              private notification: NotificationsService,
              private projectService: ProjectService) {
    this.form = this.formBuilder.group({
      virtual_location_id: ['', [Validators.min(0)]],
      parameters: this.formBuilder.array([]),
      partnumber: ['', []],
    });
  }

  ngOnInit() {
    this._projectSubscription = this.projectService.getCurrentProject().subscribe((project) => {
      this._project = project;
      const isActiveProject = !isNullOrUndefined(project) && !isNullOrUndefined(project.id);
      if (isActiveProject) {
        this._fileListSubscription = this.projectService.getUploadFiles(this._project)
          .subscribe((files) => {
            extractDataFromModel(files, 'item', '_embedded').forEach((file) => {
              this.fileList.push({label: file.filename, value: file._links.self.href});
            });
            this.fileList.push({label: 'empty', value: null});
          });
      }
    });
  }

  ngOnDestroy() {
    this._projectSubscription.unsubscribe();
    this._fileListSubscription.unsubscribe();
  }

  get parameterFormArray(): FormArray {
    return this.form.get('parameters') as FormArray;
  }

  get label() {
    return this._serviceEntity ? this._serviceEntity.name : '';
  }

  get dataIsReady() {
    return !isNullOrUndefined(this._serviceEntity);
  }

  hasParameters(): boolean {
    return this._serviceEntity
      && Object.keys(this._serviceEntity).includes('_embedded')
      && Object.keys(this._serviceEntity._embedded).includes('parameters');
  }

  updateEntity() {
    const requests = [];

    const projectUnitService = this.genericResourceService.getServiceForResource(this._serviceEntity._links.self.href);
    requests.push(projectUnitService.customPUT(this.form.value));

    if (this.hasParameters()) {
      const formArray = this.form.controls['parameters'] as FormArray;
      for (let i = 0; i < formArray.length; i++) {
        if (formArray.at(i).dirty) {
          const param = this._serviceEntity._embedded.parameters[i];
          const newParamValue = formArray.at(i).value.value;
          requests.push(this.parametersService.save(param, newParamValue));
        }
      }
    }

    Observable.forkJoin(requests)
      .subscribe((multiple) => {
        this.afterUpdate.emit(multiple[0] as Service);
        this.activeModal.close();
      })
    ;
  }

  deleteEntity() {
    const deleteUser = confirm('Are you sure you want to delete this serviceEntity?');
    if (deleteUser) {
      const projectUnitService = this.genericResourceService.getServiceForResource(this._serviceEntity._links.self.href);
      projectUnitService
        .remove()
        .subscribe(() => {
          this.notification.success('Unit deleted', 'The serviceEntity was successfully removed.');
          this.afterDelete.emit();
          this.activeModal.close();
        });
    }
  }

  private buildPartnumberOptions(service) {
    const options = [{
      value: null, label: '',
    }];
    if (!isNullOrUndefined(service._links.allowed_partnumbers)) {
      service._links.allowed_partnumbers.forEach((partnumber) => {
        options.push({value: partnumber.href, label: partnumber.label});
      });
    }
    return options;
  }
}



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_develop@2/app/Resources/Public/src/app/modules/models/services/components/modify-service/modify-service.component.ts